/**
 * PATH: packages/email-hub/src/feature/components/CompanyChip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-COMPANYCHIP-001
 * PURPOSE: The sender-company pill in the reader header — normal click opens
 *          company context; filtering is an explicit secondary action.
 * WHY:     Operator (2026-07-06): "Why can I not edit the practicesale
 *          company or click it right click etc". The chip was a static
 *          <span>/<button> inlined in EmailPreviewPane with no
 *          affordances beyond an optional click. Lifting it into its
 *          own component gives every reader surface (hub inbox, kanban
 *          drawer, card tab) the SAME interactive chip.
 * FLOW:
 *   ┌────────────────┐   ┌──────────────┐   ┌───────────────────────┐
 *   │ label + domain │ → │ click        │ → │ CRM-proxy peek /      │
 *   │ (+ callbacks)  │   │ right-click  │   │ explicit action menu  │
 *   └────────────────┘   └──────────────┘   └───────────────────────┘
 * RELATED:
 *   - EmailPreviewPane.tsx: the only mount (header "from" slot).
 *   - KanbanFullEmailReaderInner.tsx: wires onShowAll for the drawer.
 */

import { useCallback, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { createPortal } from 'react-dom'
import { Building2, ClipboardCopy, Filter, PanelRightOpen } from 'lucide-react'

export interface CompanyChipProps {
  /** Human display name (CRM client name or domain heuristic). */
  label: string
  /** Canonical lower-cased sender domain — copy target + the stable
   *  key the company scope filters on. */
  domain?: string | null
  /** Primary click: open the CRM-proxy company context drawer. */
  onOpenContext?: () => void
  /** Explicitly scope the surrounding list to this company. */
  onShowAll?: () => void
}

interface MenuPos {
  x: number
  y: number
}

const CHIP_CLASS =
  'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full ' +
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 max-w-[200px]'

export function CompanyChip({
  label,
  domain,
  onOpenContext,
  onShowAll,
}: CompanyChipProps) {
  const [menu, setMenu] = useState<MenuPos | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  // Close on click-away / Escape — standard lightweight popover contract.
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(!!menu, () => setMenu(null), [menuRef])

  const openMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    // Stop the reader-body context menu (EmailContextMenuProvider) from
    // also opening — the chip's own menu wins on the chip.
    e.stopPropagation()
    setMenu({ x: e.clientX, y: e.clientY })
  }, [])

  const copy = useCallback((text: string) => {
    void navigator.clipboard?.writeText(text).catch(() => {})
    setMenu(null)
  }, [])

  const itemClass =
    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ' +
    'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800'

  return (
    <>
      <button
        type="button"
        onClick={onOpenContext ?? onShowAll}
        onContextMenu={openMenu}
        title={
          onOpenContext
            ? `Open ${label} context — right-click for actions`
            : onShowAll
            ? `Show all emails from ${label} — right-click for more`
            : `${label} — right-click for actions`
        }
        className={
          CHIP_CLASS +
          (onOpenContext || onShowAll
            ? ' hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors cursor-pointer'
            : ' cursor-context-menu')
        }
      >
        <Building2 className="h-2.5 w-2.5 shrink-0" />
        <span className="truncate">{label}</span>
      </button>
      {menu &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={`Actions for ${label}`}
            style={{ top: menu.y, left: menu.x }}
            className="fixed z-[9999] min-w-[190px] overflow-hidden rounded-lg border border-neutral-200 bg-white py-1 shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
          >
            {onOpenContext && (
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  setMenu(null)
                  onOpenContext()
                }}
              >
                <PanelRightOpen className="h-3.5 w-3.5 shrink-0" />
                Open company context
              </button>
            )}
            {onShowAll && (
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  setMenu(null)
                  onShowAll()
                }}
              >
                <Filter className="h-3.5 w-3.5 shrink-0" />
                Show all from {label}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={itemClass}
              onClick={() => copy(label)}
            >
              <ClipboardCopy className="h-3.5 w-3.5 shrink-0" />
              Copy company name
            </button>
            {domain && (
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => copy(domain)}
              >
                <ClipboardCopy className="h-3.5 w-3.5 shrink-0" />
                Copy domain ({domain})
              </button>
            )}
          </div>,
          document.body,
        )}
    </>
  )
}

export default CompanyChip
